/**
 * Date utility functions
 */

/**
 * Checks if the current date is within the London Book Fair 2025 promotion period
 * The fair runs from March 7-9, 2025, with promotion period extended to March 30, 2025
 * 
 * @returns {boolean} - True if the current date is within the promotion period
 */
export const isLondonBookFairActive = () => {
    const now = new Date();
    
    // For development purposes, we'll consider the fair active in 2024 as well
    // Remove this condition for production
    if (now.getFullYear() === 2024) {
        return false; // Temporarily disabled to test Summer Welcome dashboard
    }
    
    // London Book Fair 2025 promotion period: March 7, 2025 - March 30, 2025
    const startDate = new Date('2025-03-07T00:00:00');
    const endDate = new Date('2025-03-30T23:59:59');
    
    return now >= startDate && now <= endDate;
};

/**
 * Checks if the current date is within the Summer Welcome Dashboard period
 * Runs from June 1, 2025 to October 1, 2025
 * 
 * @returns {boolean} - True if the current date is within the summer period
 */
export const isSummerWelcomeActive = () => {
    const now = new Date();
    
    // For development purposes, we'll consider it active in 2024 as well
    // Remove this condition for production
    if (now.getFullYear() === 2024) {
        return true; // Set to true for testing in 2024
    }
    
    // Summer Welcome Dashboard period: June 1, 2025 - October 1, 2025
    const startDate = new Date('2025-06-01T00:00:00');
    const endDate = new Date('2025-10-01T23:59:59');
    
    return now >= startDate && now <= endDate;
};

/**
 * Formats a date as a string in the format "Month Day, Year"
 * 
 * @param {Date} date - The date to format
 * @returns {string} - The formatted date string
 */
export const formatDate = (date) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return date.toLocaleDateString('en-US', options);
};

/**
 * Gets the number of days remaining until the London Book Fair 2025
 * 
 * @returns {number} - The number of days remaining, or 0 if the fair has started
 */
export const getDaysUntilLondonBookFair = () => {
    const now = new Date();
    const fairStartDate = new Date('2025-03-07T00:00:00');
    
    // If the fair has already started, return 0
    if (now >= fairStartDate) {
        return 0;
    }
    
    // Calculate the difference in days
    const diffTime = fairStartDate - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays;
};

/**
 * Gets the number of days remaining until summer period ends
 * 
 * @returns {number} - The number of days remaining, or 0 if period has ended
 */
export const getDaysUntilSummerEnd = () => {
    const now = new Date();
    const summerEndDate = new Date('2025-10-01T23:59:59');
    
    // If the period has already ended, return 0
    if (now >= summerEndDate) {
        return 0;
    }
    
    // Calculate the difference in days
    const diffTime = summerEndDate - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays;
};

/**
 * For testing purposes - allows overriding the current date
 * 
 * @param {Date} mockDate - The date to use for testing
 * @returns {boolean} True if the mock date is within the promotion period
 */
export const isLondonBookFairPromoActiveForDate = (mockDate) => {
    const startDate = new Date('2025-03-07T00:00:00');
    const endDate = new Date('2025-03-30T23:59:59');
    
    return mockDate >= startDate && mockDate <= endDate;
};

/**
 * For testing purposes - allows overriding the current date for summer dashboard
 * 
 * @param {Date} mockDate - The date to use for testing
 * @returns {boolean} True if the mock date is within the summer period
 */
export const isSummerWelcomeActiveForDate = (mockDate) => {
    const startDate = new Date('2025-06-01T00:00:00');
    const endDate = new Date('2025-10-01T23:59:59');
    
    return mockDate >= startDate && mockDate <= endDate;
}; 